/* GASSISA
=========================================================================================
* Descripción: Asistencia en piso con IA para precios y existencias
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1        | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Se integran acciones de cémara al menú principal
*      3        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Se agrega lazo IA con validación automética al 90%
*      4        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Se agrega selección automética por rayos desde punto tocado
*      5        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Se amplía el recorte de verificación para cubrir el objeto completo
*      6        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Se ajusta el recorte automético para evitar contaminación de fondo
*      7        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Se sincroniza umbral de confianza del verificador con backend IA
*      8        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Se corrige Tooltip disabled y se agregan logs de predicción
*      9        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Se limpia predicción por búsqueda y se evita consultar IA con zonas vacías
*      10       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Se retira la cruz central del visor de cémara IA
*      11       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Se renombra módulo a Asistencia en piso
*      12       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Se consulta ficha de artículo desde backend IA
*      13       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Se restaura precisión mínima de reconocimiento IA al 90%
*      14       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Se agrega carrito temporal para firma de preventas desde asistencia en piso
*      15       | 03/06/2026 | Lic. José Luis Aragón Cervantes  | Se agrega lectura de código de barras con cémara
*      16       | 03/06/2026 | Lic. José Luis Aragón Cervantes  | Se agregan beeps de lectura con cémara
*      17       | 04/06/2026 | Lic. José Luis Aragón Cervantes  | Se agrega lector físico al carrito temporal
*      18       | 04/06/2026 | Lic. José Luis Aragón Cervantes  | Ajusta aviso de soporte para lector con cémara
*      19       | 04/06/2026 | Lic. José Luis Aragón Cervantes  | Reubica acciones de carrito sobre almacén origen
*      20       | 04/06/2026 | Lic. José Luis Aragón Cervantes  | Agrega búsqueda manual al carrito temporal
*      21       | 04/06/2026 | Lic. José Luis Aragón Cervantes  | Usa buscador simple para cargar existencias
*      22       | 04/06/2026 | Lic. José Luis Aragón Cervantes  | Consulta ficha completa al seleccionar artículo
*      23       | 04/06/2026 | Lic. José Luis Aragón Cervantes  | Retira alerta de existencias en lectura por código
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import Autocomplete from "../ui/Autocomplete";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Accordion, AccordionDetails, AccordionSummary, Card, CardContent, Chip, CircularProgress, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FlipCameraIosIcon from "@mui/icons-material/FlipCameraIos";
import HomeIcon from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import VideocamIcon from "@mui/icons-material/Videocam";

import SizeSoftXLayout from "../leyout/SizeSoftXLayout.jsx";
import SizeSoftXAlert from "../utils/SizeSoftXAlert.jsx";
import SizeSoftXLectorCodigoBarrasGeneral from "../leyout/SizeSoftXLectorCodigoBarrasGeneral";
import SizeSoftXLoadingProcess from "../leyout/SizeSoftXLoadingProcess.jsx";
import ModalBuscarArticulosSimple from "../herramientas/ModalBuscarArticulosSimple";
import { useIAEntrenamiento } from "../../context/IA/IAEntrenamientoContext";
import { useLayout } from "../../context/System/LayoutContext";
import { useRequest } from "../../context/System/RequestContext";
import { useSesion } from "../../context/System/SesionContext";
import { useVentas } from "../../context/Ventas/VentasContext.js";
import {
  apiAsistenciaPisoArticulo,
  apiBuscarArticuloPorCodigoBarras,
  apiFirmarPreventa,
  apiMontoEnLetra,
  apiRegistrarArticuloPorEscaneo,
  apiSumarPartidaVenta,
  apiVentasList,
} from "../../settings/ApiConfig";
import { NumberFormat$ } from "../../settings/utils";
import { useDeviceType } from "../../hooks/useDeviceType";
import { useFormTabLock } from "../../hooks/useFormTabLock";
import CameraObjectSelectorOverlay from "../ia/CameraObjectSelectorOverlay";
import {
  captureVideoFrame,
  createObjectEnvelopeSelection,
  createObjectSelectionFromSeedRays,
  createTrackingTemplate,
  getCameraPoint,
  trackObjectSelection,
} from "../ia/utils/cameraSelection";

export default function FrmAsistenciaPiso() {
  const { isLocked: isFormLocked } = useFormTabLock("asistencia_piso", {
    title: "Asistencia en piso",
    onLockDetected: (info) => {
      console.warn(`El formulario de ${info?.title || "Asistencia en piso"} ya está abierto en otra pestaña`);
    },
  });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const objectSelectionRef = useRef(null);
  const trackingTemplateRef = useRef(null);
  const trackingAnimationRef = useRef(null);
  const barcodeDetectorRef = useRef(null);
  const barcodeScanAnimationRef = useRef(null);
  const barcodeScanBusyRef = useRef(false);
  const ultimoCodigoCamaraRef = useRef("");
  const audioContextRef = useRef(null);
  const lectorCodigoBarrasRef = useRef(null);

  const isMobile = useDeviceType();
  const { GetRequest, PostRequest } = useRequest();
  const { handlePredecir } = useIAEntrenamiento();
  const { handleOpenAlert } = useLayout();
  const { handleClickRegresarMainDashboard } = useVentas();
  const { parametrosCajaForm, handleObtenerParametrosCaja, usuarioData } = useSesion();

  const [useFrontCamera, setUseFrontCamera] = useState(false);
  const [cameraStatus, setCameraStatus] = useState("idle");
  const [cameraError, setCameraError] = useState("");
  const [videoInputCount, setVideoInputCount] = useState(0);
  const [statusWaiting, setStatusWaiting] = useState(false);
  const [articulo, setArticulo] = useState(null);
  const [ultimaClave, setUltimaClave] = useState("");
  const [objectSelection, setObjectSelection] = useState(null);
  const [livePrediction, setLivePrediction] = useState(null);
  const [almacenSeleccionado, setAlmacenSeleccionado] = useState("");
  const [medidaSeleccionada, setMedidaSeleccionada] = useState(null);
  const [carritoPreventa, setCarritoPreventa] = useState([]);
  const [partidaCarritoSeleccionada, setPartidaCarritoSeleccionada] = useState(null);
  const [montoEnLetra, setMontoEnLetra] = useState("");
  const [anchorOtherActions, setAnchorOtherActions] = useState(null);
  const [tablaMedidasOpen, setTablaMedidasOpen] = useState(false);
  const [cameraAccordionOpen, setCameraAccordionOpen] = useState(false);
  const [barcodeDetectorDisponible, setBarcodeDetectorDisponible] = useState(false);
  const [barcodeScannerActivo, setBarcodeScannerActivo] = useState(false);
  const [barcodeScannerStatus, setBarcodeScannerStatus] = useState("");
  const [ultimoCodigoCamara, setUltimoCodigoCamara] = useState("");
  const [openModalBuscarArticulos, setOpenModalBuscarArticulos] = useState(false);

  const carritoStorageKey = "sizesoftx_asistencia_piso_carrito";

  const normalizarTexto = (value) => (value || "").toString().trim().toUpperCase();

  const getUsuarioActual = () => (
    usuarioData?.Usuario
    || usuarioData?.usuario
    || usuarioData?.Nombre
    || usuarioData?.nombre
    || ""
  );

  const mismaPartida = (a, b) => (
    normalizarTexto(a?.clavearticulo) === normalizarTexto(b?.clavearticulo)
    && Number(a?.medida) === Number(b?.medida)
    && normalizarTexto(a?.almacenexhibidor) === normalizarTexto(b?.almacenexhibidor)
  );

  const refocusLectorCodigoBarras = useCallback((delay = 80) => {
    setTimeout(() => {
      lectorCodigoBarrasRef.current?.focusAndSelect?.();
    }, delay);
  }, []);

  const focusFiltroBuscadorArticulos = () => {
    setTimeout(() => {
      const filtroInput = document.querySelector('input[name="Filtro"]');
      if (filtroInput) {
        filtroInput.focus();
        filtroInput.select();
      }
    }, 150);
  };

  const reproducirBeep = (tipo = "ok") => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;

      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContextClass();
      }

      const audioContext = audioContextRef.current;
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      const inicio = audioContext.currentTime;
      const frecuencia = tipo === "ok" ? 1046 : 220;
      const duracion = tipo === "ok" ? 0.12 : 0.22;

      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(frecuencia, inicio);
      gain.gain.setValueAtTime(0.0001, inicio);
      gain.gain.exponentialRampToValueAtTime(tipo === "ok" ? 0.22 : 0.16, inicio + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, inicio + duracion);

      oscillator.connect(gain);
      gain.connect(audioContext.destination);
      oscillator.start(inicio);
      oscillator.stop(inicio + duracion + 0.02);
    } catch (error) {
      console.warn("[AsistenciaPiso][Beep]", error);
    }
  };

  useEffect(() => {
    let mounted = true;
    const validarDetector = async () => {
      if (!("BarcodeDetector" in window)) {
        setBarcodeDetectorDisponible(false);
        return;
      }

      try {
        const formatos = await window.BarcodeDetector.getSupportedFormats?.();
        const formatosLectura = formatos || [];
        const soportaFormato = formatosLectura.length === 0
          || ["code_128", "code_39", "ean_13", "ean_8", "upc_a", "upc_e", "itf"].some((formato) => formatosLectura.includes(formato));

        if (mounted) {
          setBarcodeDetectorDisponible(soportaFormato);
        }
      } catch {
        if (mounted) {
          setBarcodeDetectorDisponible(true);
        }
      }
    };

    validarDetector();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    objectSelectionRef.current = objectSelection;
  }, [objectSelection]);

  useEffect(() => {
    try {
      const carritoGuardado = JSON.parse(localStorage.getItem(carritoStorageKey) || "[]");
      if (Array.isArray(carritoGuardado)) {
        setCarritoPreventa(carritoGuardado);
      }
    } catch {
      localStorage.removeItem(carritoStorageKey);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(carritoStorageKey, JSON.stringify(carritoPreventa));
  }, [carritoPreventa]);

  useEffect(() => {
    if (parametrosCajaForm?.caja === "CAJA-DEFAULT") {
      handleObtenerParametrosCaja();
    }
  }, [parametrosCajaForm?.caja]);

  useEffect(() => {
    let lastTracking = 0;
    const ejecutarSeguimiento = (timestamp) => {
      if (
        videoRef.current
        && objectSelectionRef.current
        && trackingTemplateRef.current
        && timestamp - lastTracking > 85
      ) {
        const nextSelection = trackObjectSelection({
          videoElement: videoRef.current,
          currentSelection: objectSelectionRef.current,
          template: trackingTemplateRef.current,
          mirror: useFrontCamera,
          searchRadiusPct: 0.045,
          searchSteps: 3,
        });

        objectSelectionRef.current = nextSelection;
        setObjectSelection(nextSelection);
        lastTracking = timestamp;
      }

      trackingAnimationRef.current = requestAnimationFrame(ejecutarSeguimiento);
    };

    trackingAnimationRef.current = requestAnimationFrame(ejecutarSeguimiento);

    return () => {
      if (trackingAnimationRef.current) {
        cancelAnimationFrame(trackingAnimationRef.current);
      }
    };
  }, [useFrontCamera]);

  const detenerCamara = () => {
    setBarcodeScannerActivo(false);
    const activeStream = streamRef.current || videoRef.current?.srcObject;
    if (activeStream) {
      activeStream.getTracks().forEach((track) => track.stop());
    }
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const getCameraErrorMessage = (error) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      return "Este navegador no permite abrir la cémara desde la web.";
    }
    if (!window.isSecureContext) {
      return "La cémara requiere abrir SizeSoftX desde HTTPS o localhost.";
    }
    if (error?.name === "NotAllowedError" || error?.name === "PermissionDeniedError") {
      return "Permiso de cémara denegado. Active el permiso de cémara del navegador.";
    }
    if (error?.name === "NotFoundError" || error?.name === "DevicesNotFoundError") {
      return "No se encontró una cémara disponible en el dispositivo.";
    }
    if (error?.name === "NotReadableError" || error?.name === "TrackStartError") {
      return "La cémara esté ocupada por otra aplicación o pestaña.";
    }
    return "No se pudo iniciar la cémara. Revise permisos del navegador.";
  };

  const iniciarCamara = async () => {
    try {
      setCameraStatus("requesting");
      setCameraError("");

      if (!navigator.mediaDevices?.getUserMedia || !window.isSecureContext) {
        throw new Error("CAMERA_UNAVAILABLE");
      }

      detenerCamara();

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: useFrontCamera ? "user" : "environment" },
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      });

      streamRef.current = mediaStream;
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play().catch(() => {});
      }
      const devices = await navigator.mediaDevices.enumerateDevices().catch(() => []);
      setVideoInputCount(devices.filter((device) => device.kind === "videoinput").length);
      setCameraStatus("ready");
    } catch (error) {
      console.error("[VerificadorIA][Camara]", error);
      detenerCamara();
      setCameraStatus("error");
      setCameraError(getCameraErrorMessage(error));
    }
  };

  useEffect(() => {
    iniciarCamara();
    return () => detenerCamara();
  }, [useFrontCamera]);

  useEffect(() => {
    if (isFormLocked) {
      alert("El formulario de Asistencia en piso ya está abierto en otra pestaña del navegador.\n\nPor favor, cierre la otra ventana o pestaña para continuar.");
      setTimeout(() => {
        handleClickRegresarMainDashboard();
      }, 900);
    }
  }, [isFormLocked]);

  const getClaveArticulo = (data) => (
    data?.clavearticulo
    || data?.claveArticulo
    || data?.ClaveArticulo
    || data?.sClaveArticulo
    || data?.SClaveArticulo
    || ""
  );

  const getDescripcionArticulo = (data) => (
    data?.descripcion
    || data?.descripcionarticulo
    || data?.descripcionArticulo
    || data?.sDescripcion
    || ""
  );

  const labelObjetoIA = useMemo(() => {
    const clave = getClaveArticulo(livePrediction).trim();
    const descripcion = getDescripcionArticulo(livePrediction).trim();
    const confianza = livePrediction?.confianza !== undefined
      ? ` ${Math.round(Number(livePrediction.confianza) * 100)}%`
      : "";

    if (!clave) return "Objeto IA";
    return `${clave}${descripcion ? ` | ${descripcion}` : ""}${confianza}`;
  }, [livePrediction]);

  const limpiarResultadoIA = () => {
    setArticulo(null);
    setUltimaClave("");
    setLivePrediction(null);
    setAlmacenSeleccionado("");
    setMedidaSeleccionada(null);
  };

  const esSeleccionVerificable = (selection) => {
    if (!selection) return false;

    const area = selection.widthPct * selection.heightPct;
    const dimensionesValidas = selection.widthPct >= 0.08
      && selection.heightPct >= 0.08
      && selection.widthPct <= 0.58
      && selection.heightPct <= 0.68
      && area >= 0.012
      && area <= 0.34;
    const tieneContorno = !selection.contourPoints || selection.contourPoints.length >= 8;

    return dimensionesValidas && tieneContorno;
  };

  const bytesToImage = (value) => {
    if (typeof value === "string") {
      const imageValue = value.trim();
      if (!imageValue) return null;
      return imageValue.startsWith("data:image/")
        ? imageValue
        : `data:image/png;base64,${imageValue}`;
    }

    if (!Array.isArray(value) || value.length === 0) return null;
    let binary = "";
    for (let i = 0; i < value.length; i += 1) {
      binary += String.fromCharCode(value[i]);
    }
    return `data:image/png;base64,${window.btoa(binary)}`;
  };

  const consultarArticuloVerificador = async (claveArticulo) => {
    const clave = claveArticulo?.trim().toUpperCase() || "";
    if (!clave) {
      handleOpenAlert("No se pudo identificar la clave del artículo");
      return null;
    }

    setStatusWaiting(true);
    const response = await GetRequest({
      url: apiAsistenciaPisoArticulo.replace("{clavearticulo}", encodeURIComponent(clave)),
      loader: "Consultando verificador IA",
    });
    setStatusWaiting(false);

    if (response?.hasError || response?.haserror) {
      setArticulo(null);
      handleOpenAlert(response.message || "No se encontraron datos del artículo");
      return null;
    }

    const result = response?.result?.articulo ?? response?.result ?? null;
    setArticulo(result);
    setUltimaClave(clave);
    return result;
  };

  const capturarRecorteIA = (quality = 0.78) => captureVideoFrame({
    videoElement: videoRef.current,
    canvasElement: canvasRef.current,
    selection: objectSelectionRef.current,
    mirror: useFrontCamera,
    quality,
  });

  const verificarImagenIA = async (imagenBase64, mostrarAlerta = true) => {
    if (!imagenBase64 || statusWaiting) return null;

    limpiarResultadoIA();

    setStatusWaiting(true);
    const prediccion = await handlePredecir(imagenBase64, {
      silent: true,
      minConfidence: 0.90,
      debugLabel: "VerificadorIA.Prediccion",
    });
    setStatusWaiting(false);
    setLivePrediction(prediccion);

    const clave = getClaveArticulo(prediccion);
    if (!clave) {
      setArticulo(null);
      setUltimaClave("");
      if (mostrarAlerta) {
        handleOpenAlert("No se pudo identificar el artículo con precisión suficiente");
      }
      return null;
    }

    return consultarArticuloVerificador(clave);
  };

  const capturarYVerificar = async () => {
    if (!videoRef.current || !canvasRef.current || cameraStatus !== "ready" || statusWaiting) return;
    if (!objectSelectionRef.current) {
      handleOpenAlert("Toque el artículo en la cémara para verificar con IA");
      return;
    }

    if (!esSeleccionVerificable(objectSelectionRef.current)) {
      limpiarResultadoIA();
      handleOpenAlert("Seleccione el centro del artículo para verificar con IA");
      return;
    }

    const imagenBase64 = capturarRecorteIA(0.78);
    await verificarImagenIA(imagenBase64, true);
  };

  const seleccionarPorRayosYVerificar = async (event) => {
    if (!videoRef.current || cameraStatus !== "ready" || statusWaiting) return;
    event.preventDefault();
    limpiarResultadoIA();

    const seedPoint = getCameraPoint(event, videoRef.current);
    const raySelection = createObjectSelectionFromSeedRays({
      videoElement: videoRef.current,
      seedPoint,
      mirror: useFrontCamera,
    });
    const verificationSelection = raySelection
      ? {
        ...createObjectEnvelopeSelection(raySelection, 0.045),
        debugRays: raySelection.debugRays,
      }
      : null;
    const trackingTemplate = verificationSelection
      ? createTrackingTemplate({
        videoElement: videoRef.current,
        selection: verificationSelection,
        mirror: useFrontCamera,
      })
      : null;
    const selectionWithTracking = verificationSelection && trackingTemplate?.radialDebugRays
      ? { ...verificationSelection, debugRays: trackingTemplate.radialDebugRays }
      : verificationSelection;

    objectSelectionRef.current = selectionWithTracking;
    setObjectSelection(selectionWithTracking);
    trackingTemplateRef.current = trackingTemplate;

    if (selectionWithTracking) {
      if (!esSeleccionVerificable(selectionWithTracking)) {
        objectSelectionRef.current = null;
        setObjectSelection(null);
        trackingTemplateRef.current = null;
        handleOpenAlert("Seleccione el centro del artículo para verificar con IA");
        return;
      }

      const imagenBase64 = capturarRecorteIA(0.78);
      await verificarImagenIA(imagenBase64, true);
      return;
    }

    handleOpenAlert("No se pudo generar la envoltura del artículo");
  };

  const handleLimpiar = async () => {
    setBarcodeScannerActivo(false);
    setBarcodeScannerStatus("");
    setUltimoCodigoCamara("");
    ultimoCodigoCamaraRef.current = "";
    limpiarResultadoIA();
    setObjectSelection(null);
    objectSelectionRef.current = null;
    trackingTemplateRef.current = null;
    await iniciarCamara();
  };

  const almacenesDisponibles = useMemo(() => {
    const medidas = articulo?.listamedidas || [];
    const almacenes = new Set();

    medidas.forEach((medida) => {
      (medida.existencias || []).forEach((existencia) => {
        const almacen = normalizarTexto(existencia.almacenexhibidor);
        if (almacen) almacenes.add(almacen);
      });
    });

    return Array.from(almacenes).sort();
  }, [articulo]);

  useEffect(() => {
    if (!articulo) return;
    if (almacenesDisponibles.length === 0) {
      setAlmacenSeleccionado("");
      return;
    }

    const dominio = normalizarTexto(parametrosCajaForm?.dominio);
    const almacenInicial = almacenesDisponibles.includes(dominio)
      ? dominio
      : almacenesDisponibles[0];

    setAlmacenSeleccionado(almacenInicial);
    setMedidaSeleccionada(null);
  }, [articulo, almacenesDisponibles, parametrosCajaForm?.dominio]);

  const rows = useMemo(() => {
    const medidas = articulo?.listamedidas || [];
    return medidas.map((medida) => ({
      medida: medida.medida ?? 0,
      preciodefinitivo: Number(medida.preciodefinitivo ?? 0),
      existenciatotal: Number(medida.existenciatotal ?? 0),
      codigobarras: medida.codigobarras || "",
      existenciaalmacen: Number((medida.existencias || [])
        .find((existencia) => normalizarTexto(existencia.almacenexhibidor) === normalizarTexto(almacenSeleccionado))
        ?.existencia || 0),
      almacenexhibidor: almacenSeleccionado,
      almacenes: (medida.existencias || [])
        .map((existencia) => `${existencia.almacenexhibidor}: ${Number(existencia.existencia || 0)}`)
        .join(" | "),
    }));
  }, [articulo, almacenSeleccionado]);

  const totalesCarrito = useMemo(() => {
    const totalArticulos = carritoPreventa.reduce((sum, partida) => sum + Number(partida.cantidad || 0), 0);
    const total = carritoPreventa.reduce((sum, partida) => (
      sum + (Number(partida.preciodefinitivo || 0) * Number(partida.cantidad || 0))
    ), 0);

    return {
      totalArticulos,
      totalRegistros: carritoPreventa.length,
      total,
    };
  }, [carritoPreventa]);

  const cantidadCarritoMedidaSeleccionada = useMemo(() => {
    if (!articulo || !medidaSeleccionada || !almacenSeleccionado) return 0;

    const partidaActual = {
      clavearticulo: normalizarTexto(articulo.clavearticulo || ultimaClave),
      medida: Number(medidaSeleccionada.medida || 0),
      almacenexhibidor: normalizarTexto(almacenSeleccionado),
    };

    const partida = carritoPreventa.find((item) => mismaPartida(item, partidaActual));
    return Number(partida?.cantidad || 0);
  }, [articulo, ultimaClave, medidaSeleccionada, almacenSeleccionado, carritoPreventa]);

  const puedeAgregarPartida = Boolean(
    articulo
    && medidaSeleccionada
    && almacenSeleccionado
    && medidaSeleccionada?.codigobarras
    && Number(medidaSeleccionada?.existenciaalmacen || 0) > cantidadCarritoMedidaSeleccionada
    && !statusWaiting
  );

  useEffect(() => {
    const consultarMontoLetra = async () => {
      if (totalesCarrito.total <= 0) {
        setMontoEnLetra("");
        return;
      }

      const response = await GetRequest({
        url: apiMontoEnLetra.replace("{monto}", totalesCarrito.total),
      });

      if (!response?.hasError && response?.result) {
        setMontoEnLetra(response.result);
      }
    };

    consultarMontoLetra();
  }, [totalesCarrito.total]);

  const handleSeleccionarMedida = (row) => {
    setMedidaSeleccionada(row);
  };

  const seleccionarMedidaPorCodigoBarras = (articuloConsultado, codigoBarras, almacenPreferido = "") => {
    const codigo = normalizarTexto(codigoBarras);
    const medida = (articuloConsultado?.listamedidas || []).find((item) => (
      normalizarTexto(item?.codigobarras || item?.codigoBarras) === codigo
    ));

    if (!medida) return null;

    const almacenSolicitado = normalizarTexto(almacenPreferido);
    const dominio = normalizarTexto(parametrosCajaForm?.dominio);
    const almacen = (medida.existencias || []).some((existencia) => normalizarTexto(existencia.almacenexhibidor) === almacenSolicitado)
      ? almacenSolicitado
      : (medida.existencias || []).some((existencia) => normalizarTexto(existencia.almacenexhibidor) === dominio)
        ? dominio
        : normalizarTexto(medida.existencias?.[0]?.almacenexhibidor);

    const medidaRow = {
      medida: medida.medida ?? 0,
      preciodefinitivo: Number(medida.preciodefinitivo ?? 0),
      existenciatotal: Number(medida.existenciatotal ?? 0),
      codigobarras: medida.codigobarras || medida.codigoBarras || "",
      existenciaalmacen: Number((medida.existencias || [])
        .find((existencia) => normalizarTexto(existencia.almacenexhibidor) === normalizarTexto(almacen))
        ?.existencia || 0),
      almacenexhibidor: almacen || "",
      almacenes: (medida.existencias || [])
        .map((existencia) => `${existencia.almacenexhibidor}: ${Number(existencia.existencia || 0)}`)
        .join(" | "),
    };

    setAlmacenSeleccionado(almacen || "");
    setMedidaSeleccionada(medidaRow);
    return medidaRow;
  };

  const agregarPartidaTemporal = (nuevaPartida) => {
    setCarritoPreventa((prev) => {
      const existe = prev.find((partida) => mismaPartida(partida, nuevaPartida));
      if (!existe) return [...prev, nuevaPartida];

      return prev.map((partida) => (
        mismaPartida(partida, nuevaPartida)
          ? { ...partida, cantidad: Number(partida.cantidad || 0) + 1, fechahorapreventa: nuevaPartida.fechahorapreventa }
          : partida
      ));
    });
  };

  const registrarArticuloCodigoBarrasEnTemporal = async (articuloLeido, codigoBarras, almacenPreferido = "") => {
    const codigo = (codigoBarras || articuloLeido?.codigobarras || articuloLeido?.codigoBarras || "").toString().trim();
    if (!codigo) {
      reproducirBeep("error");
      handleOpenAlert("No se recibió código de barras para registrar");
      refocusLectorCodigoBarras();
      return;
    }

    const clave = getClaveArticulo(articuloLeido);
    const articuloConsultado = clave && !Array.isArray(articuloLeido?.listamedidas)
      ? await consultarArticuloVerificador(clave)
      : articuloLeido;
    const articuloRegistro = articuloConsultado || articuloLeido;

    if (!articuloRegistro) {
      reproducirBeep("error");
      handleOpenAlert("No se encontraron datos del artículo leído");
      refocusLectorCodigoBarras();
      return;
    }

    if (!clave) {
      setArticulo(articuloRegistro);
      setUltimaClave(getClaveArticulo(articuloRegistro));
    }

    const medida = seleccionarMedidaPorCodigoBarras(articuloRegistro, codigo, almacenPreferido);
    if (!medida) {
      reproducirBeep("error");
      handleOpenAlert("No se encontró la medida del código leído");
      refocusLectorCodigoBarras();
      return;
    }

    const partidaBase = {
      clavearticulo: normalizarTexto(articuloRegistro.clavearticulo || articuloRegistro.claveArticulo || ultimaClave || clave),
      medida: Number(medida.medida || 0),
      almacenexhibidor: normalizarTexto(medida.almacenexhibidor),
    };
    const cantidadActual = Number(carritoPreventa.find((item) => mismaPartida(item, partidaBase))?.cantidad || 0);

    if (Number(medida.existenciaalmacen || 0) <= cantidadActual) {
      reproducirBeep("error");
      handleOpenAlert(`Sin existencia disponible: Medida ${medida.medida} en ${medida.almacenexhibidor} (existencia: ${medida.existenciaalmacen || 0})`);
      refocusLectorCodigoBarras();
      return;
    }

    agregarPartidaTemporal({
      clavearticulo: partidaBase.clavearticulo,
      descripcion: articuloRegistro.descripcion || getDescripcionArticulo(articuloRegistro),
      medida: partidaBase.medida,
      codigobarras: medida.codigobarras || codigo,
      almacenexhibidor: partidaBase.almacenexhibidor,
      cantidad: 1,
      preciodefinitivo: Number(medida.preciodefinitivo || 0),
      existenciaactual: Number(medida.existenciaalmacen || 0),
      fechahorapreventa: new Date().toISOString(),
      usuario: getUsuarioActual(),
    });

    reproducirBeep("ok");
    handleOpenAlert("Artículo agregado al carrito temporal", "success");
    refocusLectorCodigoBarras();
  };

  const consultarArticuloPorCodigoBarrasCamara = async (codigoBarras) => {
    const codigo = (codigoBarras || "").toString().trim();
    if (!codigo || statusWaiting) return;
    if (ultimoCodigoCamaraRef.current === codigo) return;

    ultimoCodigoCamaraRef.current = codigo;
    setUltimoCodigoCamara(codigo);
    setBarcodeScannerStatus(`Código detectado: ${codigo}`);
    setBarcodeScannerActivo(false);

    setStatusWaiting(true);
    const response = await GetRequest({
      url: apiBuscarArticuloPorCodigoBarras.replace("{codigobarras}", encodeURIComponent(codigo)),
      loader: "Consultando código de barras...",
    });
    setStatusWaiting(false);

    if (response?.hasError || !response?.result) {
      ultimoCodigoCamaraRef.current = "";
      reproducirBeep("error");
      handleOpenAlert(response?.message || "No se encontró artículo para el código leído");
      return;
    }

    const articuloCodigo = response.result;
    const clave = getClaveArticulo(articuloCodigo);
    if (!clave) {
      await registrarArticuloCodigoBarrasEnTemporal(articuloCodigo, codigo);
      return;
    }

    const articuloConsultado = await consultarArticuloVerificador(clave);
    await registrarArticuloCodigoBarrasEnTemporal(articuloConsultado || articuloCodigo, codigo);
  };

  useEffect(() => {
    if (!barcodeScannerActivo || cameraStatus !== "ready") return undefined;

    let cancelado = false;
    const formatosPreferidos = ["code_128", "code_39", "ean_13", "ean_8", "upc_a", "upc_e", "itf"];

    const prepararDetector = async () => {
      if (!("BarcodeDetector" in window)) {
        setBarcodeScannerActivo(false);
        setBarcodeScannerStatus("");
        reproducirBeep("error");
        handleOpenAlert("Este navegador no soporta lectura de códigos con cémara");
        return null;
      }

      if (!barcodeDetectorRef.current) {
        try {
          const formatos = await window.BarcodeDetector.getSupportedFormats?.();
          const soportados = formatos?.filter((formato) => formatosPreferidos.includes(formato)) || formatosPreferidos;
          barcodeDetectorRef.current = new window.BarcodeDetector({ formats: soportados });
        } catch {
          barcodeDetectorRef.current = new window.BarcodeDetector();
        }
      }

      return barcodeDetectorRef.current;
    };

    const leerFrame = async () => {
      if (cancelado || !barcodeScannerActivo || barcodeScanBusyRef.current) {
        if (!cancelado) {
          barcodeScanAnimationRef.current = requestAnimationFrame(leerFrame);
        }
        return;
      }

      const detector = await prepararDetector();
      if (!detector || !videoRef.current || cancelado) return;

      try {
        barcodeScanBusyRef.current = true;
        const resultados = await detector.detect(videoRef.current);
        const codigo = resultados?.[0]?.rawValue?.trim();

        if (codigo) {
          await consultarArticuloPorCodigoBarrasCamara(codigo);
          barcodeScanBusyRef.current = false;
          return;
        }
      } catch (error) {
        console.error("[AsistenciaPiso][BarcodeDetector]", error);
      } finally {
        barcodeScanBusyRef.current = false;
      }

      if (!cancelado) {
        barcodeScanAnimationRef.current = requestAnimationFrame(leerFrame);
      }
    };

    setBarcodeScannerStatus("Apunte la cémara al código de barras");
    barcodeScanAnimationRef.current = requestAnimationFrame(leerFrame);

    return () => {
      cancelado = true;
      if (barcodeScanAnimationRef.current) {
        cancelAnimationFrame(barcodeScanAnimationRef.current);
      }
    };
  }, [barcodeScannerActivo, cameraStatus]);

  const handleToggleBarcodeScanner = () => {
    if (cameraStatus !== "ready") {
      reproducirBeep("error");
      handleOpenAlert("La cémara debe estar lista para leer códigos");
      return;
    }

    if (!barcodeDetectorDisponible) {
      reproducirBeep("error");
      handleOpenAlert("Este navegador no soporta lectura de códigos con cémara");
      return;
    }

    setBarcodeScannerActivo((prev) => !prev);
  };

  const handleRegistrarArticuloCodigoBarrasFisico = async (data) => {
    const codigo = data?.codigobarras || data?.codigoBarras || "";
    await registrarArticuloCodigoBarrasEnTemporal(data, codigo);
  };

  const handleAbrirBuscadorArticulos = () => {
    setOpenModalBuscarArticulos(true);
    focusFiltroBuscadorArticulos();
  };

  const handleCerrarBuscadorArticulos = () => {
    setOpenModalBuscarArticulos(false);
    refocusLectorCodigoBarras();
  };

  const handleCargarArticuloDesdeBuscador = async (data) => {
    setOpenModalBuscarArticulos(false);
    const clave = getClaveArticulo(data);
    const articuloConsultado = clave
      ? await consultarArticuloVerificador(clave)
      : data;

    setArticulo(articuloConsultado || data);
    setUltimaClave(clave);
    setMedidaSeleccionada(null);
    refocusLectorCodigoBarras();
  };

  const handleAgregarPartidaCarrito = () => {
    if (!articulo) {
      handleOpenAlert("Primero verifique un artículo con IA");
      return;
    }

    if (!almacenSeleccionado) {
      handleOpenAlert("Seleccione el almacén origen");
      return;
    }

    if (!medidaSeleccionada) {
      handleOpenAlert("Seleccione una medida");
      return;
    }

    if (!medidaSeleccionada.codigobarras) {
      handleOpenAlert("La medida seleccionada no tiene código de barras");
      return;
    }

    if (Number(medidaSeleccionada.existenciaalmacen || 0) <= 0) {
      handleOpenAlert("La medida seleccionada no tiene existencia en el almacén seleccionado");
      return;
    }

    const nuevaPartida = {
      clavearticulo: normalizarTexto(articulo.clavearticulo || ultimaClave),
      descripcion: articulo.descripcion || "",
      medida: Number(medidaSeleccionada.medida || 0),
      codigobarras: medidaSeleccionada.codigobarras,
      almacenexhibidor: normalizarTexto(almacenSeleccionado),
      cantidad: 1,
      preciodefinitivo: Number(medidaSeleccionada.preciodefinitivo || 0),
      existenciaactual: Number(medidaSeleccionada.existenciaalmacen || 0),
      fechahorapreventa: new Date().toISOString(),
      usuario: getUsuarioActual(),
    };

    agregarPartidaTemporal(nuevaPartida);
    refocusLectorCodigoBarras();
  };

  const handleEliminarPartidaCarrito = () => {
    if (!partidaCarritoSeleccionada) {
      handleOpenAlert("Seleccione una partida del carrito");
      return;
    }

    setCarritoPreventa((prev) => prev.filter((partida) => !mismaPartida(partida, partidaCarritoSeleccionada)));
    setPartidaCarritoSeleccionada(null);
  };

  const handleReiniciarTodo = async () => {
    await handleLimpiar();
    setCarritoPreventa([]);
    setPartidaCarritoSeleccionada(null);
    setMontoEnLetra("");
    localStorage.removeItem(carritoStorageKey);
    setAnchorOtherActions(null);
  };

  const handleFirmarPreventaCarrito = async () => {
    if (statusWaiting) return;

    const caja = parametrosCajaForm?.caja;
    const folioPreventa = parametrosCajaForm?.foliopreventa;
    const vendedor = getUsuarioActual();

    if (!caja || caja === "CAJA-DEFAULT") {
      await handleObtenerParametrosCaja();
      handleOpenAlert("No se pudo validar la caja de trabajo");
      return;
    }

    if (!folioPreventa) {
      handleOpenAlert("No se pudo validar el folio de preventa");
      return;
    }

    if (!vendedor) {
      handleOpenAlert("No se pudo validar el usuario vendedor");
      return;
    }

    if (carritoPreventa.length === 0) {
      handleOpenAlert("No hay partidas en el carrito");
      return;
    }

    setStatusWaiting(true);
    try {
      for (const partida of carritoPreventa) {
        const bodyRegistro = {
          pc: caja,
          remision: folioPreventa,
          tiporegistro: "PREVENTA",
          dominio: partida.almacenexhibidor,
          codigobarras: partida.codigobarras,
          usuario: vendedor,
          clavecliente: "",
        };

        const responseRegistro = await PostRequest({
          url: apiRegistrarArticuloPorEscaneo,
          body: bodyRegistro,
          loader: "Registrando partida de preventa...",
        });

        if (responseRegistro?.hasError || responseRegistro?.result === null) {
          handleOpenAlert(responseRegistro?.message || "No se pudo registrar la partida de preventa");
          setStatusWaiting(false);
          return;
        }

        const cantidadExtra = Number(partida.cantidad || 1) - 1;
        if (cantidadExtra > 0) {
          const responseSuma = await PostRequest({
            url: apiSumarPartidaVenta,
            body: {
              pc: caja,
              remision: folioPreventa,
              tiporegistro: "PREVENTA",
              dominio: partida.almacenexhibidor,
              codigobarras: "",
              usuario: vendedor,
              clavecliente: "",
              cantidad: cantidadExtra,
              clavearticulo: partida.clavearticulo,
              medida: partida.medida,
            },
            loader: "Sumando cantidad a partida de preventa...",
          });

          if (responseSuma?.hasError || responseSuma?.result === false) {
            handleOpenAlert(responseSuma?.message || "No se pudo sumar la cantidad a la partida");
            setStatusWaiting(false);
            return;
          }
        }
      }

      const responseLista = await PostRequest({
        url: apiVentasList,
        body: {
          folioventa: folioPreventa,
          caja,
          tipoventa: "PREVENTA",
        },
      });

      const partidasServidor = responseLista?.result?.listaremisiontemporal || [];
      if (responseLista?.hasError || partidasServidor.length === 0) {
        handleOpenAlert(responseLista?.message || "No se encontraron partidas temporales para firmar");
        setStatusWaiting(false);
        return;
      }

      const responseFirma = await PostRequest({
        url: apiFirmarPreventa,
        body: {
          foliopreventa: folioPreventa,
          vendedorpreventa: vendedor,
          caja,
        },
        loader: "Firmando preventa...",
      });

      if (responseFirma?.hasError) {
        handleOpenAlert(responseFirma.message || "No se pudo firmar la preventa");
        setStatusWaiting(false);
        return;
      }

      handleReiniciarTodo();
      await handleObtenerParametrosCaja();
      handleOpenAlert("Preventa firmada correctamente", "success");
    } finally {
      setStatusWaiting(false);
    }
  };

  const imagenArticulo = bytesToImage(
    articulo?.foto
    || articulo?.imagen
    || articulo?.imagenbase64
    || articulo?.imagenBase64
    || articulo?.sImagenBase64
  );

  const buttonActions = (
    <Fragment>
      <Tooltip title="Salir">
        <IconButton onClick={handleClickRegresarMainDashboard}>
          <HomeIcon className="icon" /><p className="icon">Salir</p>
        </IconButton>
      </Tooltip>
      <Tooltip title="Verificar por foto">
        <span>
          <IconButton
            onClick={capturarYVerificar}
            disabled={cameraStatus !== "ready" || statusWaiting || barcodeScannerActivo}
          >
            <CameraAltIcon className="icon" /><p className="icon">Verificar</p>
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={barcodeScannerActivo ? "Detener lectura de código" : "Leer código con cémara"}>
        <span>
          <IconButton
            onClick={handleToggleBarcodeScanner}
            disabled={cameraStatus !== "ready" || statusWaiting}
          >
            <QrCodeScannerIcon className="icon" /><p className="icon">Leer código</p>
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Buscar artículos">
        <span>
          <IconButton
            onClick={handleAbrirBuscadorArticulos}
            disabled={statusWaiting}
          >
            <SearchIcon className="icon" /><p className="icon">Buscar</p>
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Agregar partida al carrito">
        <span>
          <IconButton
            onClick={handleAgregarPartidaCarrito}
            disabled={!puedeAgregarPartida}
          >
            <AddShoppingCartIcon className="icon" /><p className="icon">Agregar</p>
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Firmar preventa">
        <span>
          <IconButton
            onClick={handleFirmarPreventaCarrito}
            disabled={carritoPreventa.length === 0 || statusWaiting}
          >
            <ShoppingCartCheckoutIcon className="icon" /><p className="icon">Firmar</p>
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Més acciones">
        <IconButton onClick={(event) => setAnchorOtherActions(event.currentTarget)}>
          <MenuIcon className="icon" /><p className="icon">Menu</p>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorOtherActions}
        open={Boolean(anchorOtherActions)}
        onClose={() => setAnchorOtherActions(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setUseFrontCamera((prev) => !prev);
            setAnchorOtherActions(null);
          }}
          disabled={cameraStatus === "requesting" || statusWaiting || videoInputCount < 2}
        >
          <ListItemIcon>
            <FlipCameraIosIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={useFrontCamera ? "Cémara trasera" : "Cémara frontal"} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleLimpiar();
            setAnchorOtherActions(null);
          }}
          disabled={cameraStatus === "requesting" || statusWaiting}
        >
          <ListItemIcon>
            <CleaningServicesIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Limpiar cémara" />
        </MenuItem>
        <MenuItem onClick={handleEliminarPartidaCarrito} disabled={!partidaCarritoSeleccionada || statusWaiting}>
          <ListItemIcon>
            <RemoveShoppingCartIcon color="error" fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Eliminar partida" />
        </MenuItem>
        <MenuItem onClick={handleReiniciarTodo} disabled={carritoPreventa.length === 0 || statusWaiting}>
          <ListItemIcon>
            <DeleteIcon color="error" fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Eliminar carrito" />
        </MenuItem>
      </Menu>
    </Fragment>
  );

  return (
    <Fragment>
      <SizeSoftXLayout titlePage="Asistencia en piso" buttonActions={buttonActions}>
        <Box sx={{ p: { xs: 1, sm: 2 }, pb: { xs: 8, sm: 10 }, maxWidth: 900, mx: "auto" }}>
          {!isMobile && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Este formulario esté diseñado para dispositivos portétiles con cémara.
            </Alert>
          )}

          <Card sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, "&:last-child": { pb: { xs: 1.25, sm: 1.5 } } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                divider={<Divider orientation={isMobile ? "horizontal" : "vertical"} flexItem />}
                sx={{ alignItems: { xs: "stretch", sm: "center" } }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">Caja</Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                    {parametrosCajaForm?.caja || "CAJA-DEFAULT"}
                  </Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", sm: 140 } }}>
                  <Typography variant="caption" color="text.secondary">Folio preventa</Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                    {parametrosCajaForm?.foliopreventa || 0}
                  </Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", sm: 170 } }}>
                  <Typography variant="caption" color="text.secondary">Vendedor</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                    {getUsuarioActual() || "Sin usuario"}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, "&:last-child": { pb: { xs: 1.25, sm: 1.5 } } }}>
              <SizeSoftXLectorCodigoBarrasGeneral
                ref={lectorCodigoBarrasRef}
                TipoVentana="ASISTENCIA_PISO"
                handleRegistrarArticulo={handleRegistrarArticuloCodigoBarrasFisico}
                disabled={statusWaiting}
                sx={{
                  '& h2': {
                    color: '#374151',
                    fontSize: '0.82rem',
                    fontWeight: 700,
                  },
                  '& input': {
                    width: '100% !important',
                    maxWidth: 'none !important',
                    height: '40px',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: '8px',
                    padding: '0 12px',
                    boxSizing: 'border-box',
                    background: '#fff',
                  },
                }}
              />
            </CardContent>
          </Card>

          <Accordion
            expanded={cameraAccordionOpen}
            onChange={(_, expanded) => setCameraAccordionOpen(expanded)}
            sx={{ mb: 1.5 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <VideocamIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Cémara
                </Typography>
                <Chip size="small" label={cameraStatus === "ready" ? "Lista" : "No activa"} color={cameraStatus === "ready" ? "success" : "default"} />
                <Chip size="small" label={`${videoInputCount || 0} cémara${videoInputCount === 1 ? "" : "s"}`} color={videoInputCount > 0 ? "primary" : "default"} variant="outlined" />
                {barcodeDetectorDisponible && (
                  <Chip size="small" label="Lector disponible" color="success" variant="outlined" />
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Box
                  onClick={(event) => {
                    if (barcodeScannerActivo) return;
                    seleccionarPorRayosYVerificar(event);
                  }}
                  sx={{
                    position: "relative",
                    bgcolor: "#000",
                    borderRadius: 1,
                    overflow: "hidden",
                    aspectRatio: "4/3",
                    width: "100%",
                    maxWidth: { xs: 300, sm: 360 },
                    mx: "auto",
                    cursor: cameraStatus === "ready" ? "pointer" : "default",
                    touchAction: "none",
                    userSelect: "none",
                  }}
                >
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transform: useFrontCamera ? "scaleX(-1)" : "none",
                    }}
                  />
                  <CameraObjectSelectorOverlay
                    selection={objectSelection}
                    disabled={cameraStatus !== "ready" || barcodeScannerActivo}
                    videoElement={videoRef.current}
                    label={labelObjetoIA}
                  />

                  {barcodeScannerActivo && (
                    <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                      <Box sx={{ width: "78%", height: "32%", border: "3px solid #4ade80", borderRadius: 1, boxShadow: "0 0 0 999px rgba(0,0,0,.28)" }} />
                      <Chip
                        size="small"
                        color="success"
                        label="Leyendo código"
                        sx={{ position: "absolute", top: 8, left: 8, fontWeight: 800 }}
                      />
                    </Box>
                  )}

                  {cameraStatus !== "ready" && (
                    <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,.72)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 1, p: 1, textAlign: "center" }}>
                      {cameraStatus === "requesting" ? (
                        <CircularProgress size={24} sx={{ color: "white" }} />
                      ) : (
                        <Typography variant="caption">{cameraError || "Cémara no activa"}</Typography>
                      )}
                    </Box>
                  )}
                  <canvas ref={canvasRef} style={{ display: "none" }} />
                </Box>
              </Box>
              {(barcodeScannerStatus || ultimoCodigoCamara) && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {barcodeScannerStatus || `Último código leído: ${ultimoCodigoCamara}`}
                </Alert>
              )}
            </AccordionDetails>
          </Accordion>

          <Card sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, "&:last-child": { pb: { xs: 1.25, sm: 1.5 } } }}>
              {articulo ? (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: imagenArticulo ? "120px 1fr" : "1fr" }, gap: 1.5, alignItems: "center" }}>
                  {imagenArticulo && (
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <img
                        src={imagenArticulo}
                        alt={articulo.descripcion}
                        style={{ width: 110, height: 90, objectFit: "contain", border: "1px solid #d0d0d0", borderRadius: 4 }}
                      />
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary">Artículo detectado</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                      {articulo.descripcion}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      <strong>Clave:</strong> {articulo.clavearticulo || ultimaClave}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Esperando verificación IA
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tome una foto del artículo para consultar precios y existencias.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, "&:last-child": { pb: { xs: 1.25, sm: 1.5 } } }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddShoppingCartIcon />}
                  onClick={handleAgregarPartidaCarrito}
                  disabled={!puedeAgregarPartida}
                >
                  Agregar partida
                </Button>
                <Button
                  fullWidth
                  color="error"
                  variant="outlined"
                  startIcon={<RemoveShoppingCartIcon />}
                  onClick={handleEliminarPartidaCarrito}
                  disabled={!partidaCarritoSeleccionada || statusWaiting}
                >
                  Eliminar partida
                </Button>
                <Button
                  fullWidth
                  color="success"
                  variant="contained"
                  startIcon={<ShoppingCartCheckoutIcon />}
                  onClick={handleFirmarPreventaCarrito}
                  disabled={carritoPreventa.length === 0 || statusWaiting}
                >
                  Firmar preventa
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, "&:last-child": { pb: { xs: 1.25, sm: 1.5 } } }}>
              <Stack spacing={1}>
                <Autocomplete
                  size="small"
                  options={almacenesDisponibles}
                  value={almacenSeleccionado || null}
                  onChange={(_, value) => {
                    setAlmacenSeleccionado(value || "");
                    setMedidaSeleccionada(null);
                  }}
                  disabled={!articulo || almacenesDisponibles.length === 0}
                  renderInput={(params) => (
                    <TextField {...params} label="Almacén / exhibidor origen" />
                  )}
                />
                <Autocomplete
                  size="small"
                  options={rows}
                  value={medidaSeleccionada}
                  onChange={(_, value) => setMedidaSeleccionada(value)}
                  disabled={!articulo || rows.length === 0 || !almacenSeleccionado}
                  isOptionEqualToValue={(option, value) => Number(option?.medida) === Number(value?.medida)}
                  getOptionLabel={(option) => option
                    ? `Medida ${option.medida} | ${NumberFormat$(option.preciodefinitivo)} | Existencia ${option.existenciaalmacen}`
                    : ""}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          Medida {option.medida}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.codigobarras || "Sin código de barras"}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {NumberFormat$(option.preciodefinitivo)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: option.existenciaalmacen > 0 ? "success.main" : "error.main", fontWeight: 800 }}
                        >
                          Exist. {option.existenciaalmacen}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Medida / precio / existencia" />
                  )}
                />
                {medidaSeleccionada && (
                  <Box sx={{ border: "1px solid #d7d7d7", borderRadius: 1, p: 1, bgcolor: "#fafafa" }}>
                    <Typography variant="caption" color="text.secondary">Existencias por almacén</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {medidaSeleccionada.almacenes || "Sin existencia registrada"}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Accordion
            expanded={tablaMedidasOpen}
            onChange={(_, expanded) => setTablaMedidasOpen(expanded)}
            sx={{ mb: 1.5 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Consulta completa de medidas
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {rows.length} medidas disponibles
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <TableContainer component={Paper} sx={{ maxHeight: { xs: "32vh", sm: "45vh" } }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800 }}>Medida</TableCell>
                      <TableCell sx={{ fontWeight: 800 }} align="right">Precio definitivo</TableCell>
                      <TableCell sx={{ fontWeight: 800 }} align="right">Existencia</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Almacenes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          Sin datos para mostrar
                        </TableCell>
                      </TableRow>
                    ) : rows.map((row) => (
                      <TableRow
                        key={`${row.medida}-${row.codigobarras}`}
                        hover
                        selected={Number(medidaSeleccionada?.medida) === Number(row.medida)}
                        onClick={() => handleSeleccionarMedida(row)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell sx={{ fontWeight: 700 }}>{row.medida}</TableCell>
                        <TableCell align="right">{NumberFormat$(row.preciodefinitivo)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: row.existenciaalmacen > 0 ? "success.main" : "error.main" }}>
                          {row.existenciaalmacen}
                        </TableCell>
                        <TableCell sx={{ minWidth: 180 }}>{row.almacenes || "Sin existencia registrada"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          <Card sx={{ mt: 1.5 }}>
            <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, "&:last-child": { pb: { xs: 1.25, sm: 1.5 } } }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Carrito preventa</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {totalesCarrito.totalRegistros} registros / {totalesCarrito.totalArticulos} artículos
                  </Typography>
                </Box>
                <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                  <Typography variant="caption" color="text.secondary">Total estimado</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    {NumberFormat$(totalesCarrito.total)}
                  </Typography>
                </Box>
              </Stack>

              {montoEnLetra && (
                <Typography variant="caption" sx={{ display: "block", mb: 1, fontWeight: 700 }}>
                  {montoEnLetra}
                </Typography>
              )}

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: { xs: "34vh", sm: "38vh" } }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800 }}>Clave</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Descripción</TableCell>
                      <TableCell sx={{ fontWeight: 800 }} align="right">Medida</TableCell>
                      <TableCell sx={{ fontWeight: 800 }} align="right">Cant.</TableCell>
                      <TableCell sx={{ fontWeight: 800 }} align="right">Total</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Origen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {carritoPreventa.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">Sin partidas en carrito</TableCell>
                      </TableRow>
                    ) : carritoPreventa.map((partida) => (
                      <TableRow
                        key={`${partida.clavearticulo}-${partida.medida}-${partida.almacenexhibidor}`}
                        hover
                        selected={mismaPartida(partida, partidaCarritoSeleccionada)}
                        onClick={() => setPartidaCarritoSeleccionada(partida)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{partida.clavearticulo}</TableCell>
                        <TableCell sx={{ minWidth: 180 }}>{partida.descripcion}</TableCell>
                        <TableCell align="right">{partida.medida}</TableCell>
                        <TableCell align="right">{partida.cantidad}</TableCell>
                        <TableCell align="right">{NumberFormat$(Number(partida.preciodefinitivo || 0) * Number(partida.cantidad || 0))}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{partida.almacenexhibidor}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

            </CardContent>
          </Card>
        </Box>

        <SizeSoftXAlert />
        <ModalBuscarArticulosSimple
          openModal={openModalBuscarArticulos}
          handleCloseModal={handleCerrarBuscadorArticulos}
          handleArticuloSelect={handleCargarArticuloDesdeBuscador}
        />
        <SizeSoftXLoadingProcess
          open={statusWaiting}
          message="Analizando artículo y consultando existencias..."
        />
      </SizeSoftXLayout>
    </Fragment>
  );
}
